import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { authHeaders, jsonAuthHeaders } from '../../shared/authHeaders';
import { logVideoDiagnostic } from '../video/videoDiagnostics';
import {
  clearStoredRoomSession,
  getRoomSessionDiagnosticContext,
  getStoredRoomSession,
  hasReusableStoredRoomSession,
  storeJoinedRoomSession,
  updateStoredRoomSessionFromAccess,
} from './joinSession';

function logJoinStage(code: string, stage: string, details: Record<string, unknown> = {}) {
  logVideoDiagnostic('info', stage, details, getRoomSessionDiagnosticContext(code));
}

function logJoinWarning(code: string, stage: string, details: Record<string, unknown> = {}) {
  logVideoDiagnostic('warn', stage, details, getRoomSessionDiagnosticContext(code));
}

export function JoinPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasJoinedRef = useRef(false);

  useEffect(() => {
    async function joinRoom() {
      if (!code || hasJoinedRef.current) return;

      hasJoinedRef.current = true;
      setError(null);

      try {
        const authToken = await getToken();
        logJoinStage(code, 'auth', { hasAuthToken: Boolean(authToken) });

        const storedSession = getStoredRoomSession(code);
        if (hasReusableStoredRoomSession(storedSession)) {
          const accessResponse = await fetch(`/api/rooms/${code}/access`, {
            headers: authHeaders(authToken),
          });
          const accessData = await accessResponse.json().catch(() => ({}));

          if (accessData.requiresInviteAcceptance && accessData.invitationUrl) {
            clearStoredRoomSession(code);
            navigate(accessData.invitationUrl, { replace: true });
            return;
          }

          if (accessData.requiresTicket) {
            clearStoredRoomSession(code);
            navigate(accessData.ticketUrl || `/s/${code}`, { replace: true });
            return;
          }

          if (accessData.sessionNotOpen) {
            clearStoredRoomSession(code);
            navigate(`/s/${code}`, { replace: true });
            return;
          }

          if (accessResponse.ok && accessData.canEnter === true) {
            updateStoredRoomSessionFromAccess(code, accessData);
            logJoinStage(code, 'room-access-ok', {
              source: 'stored-session',
              reusedParticipantId: storedSession.participantId,
              isHost: accessData.isHost === true,
              isInvited: accessData.isInvited === true,
              isConfirmedModel: accessData.isConfirmedModel === true,
            });
            navigate(`/s/${code}/room`, { replace: true });
            return;
          }

          logJoinWarning(code, 'join-stored-session-rejected', {
            status: accessResponse.status,
            canEnter: accessData.canEnter === true,
            hasError: typeof accessData.error === 'string',
          });
          clearStoredRoomSession(code);
        }

        const response = await fetch(`/api/rooms/${code}/join`, {
          method: 'POST',
          headers: jsonAuthHeaders(authToken),
          body: JSON.stringify({}),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (data.requiresInviteAcceptance && data.invitationUrl) {
            navigate(data.invitationUrl, { replace: true });
            return;
          }
          if (data.sessionNotOpen) {
            navigate(`/s/${code}`, { replace: true });
            return;
          }
          setError(data.error || 'Failed to join room');
          return;
        }

        if (data.requiresTicket) {
          navigate(data.ticketUrl || `/s/${code}`, { replace: true });
          return;
        }

        storeJoinedRoomSession(code, data);
        logJoinStage(code, 'join-token-created', {
          participantId: data.participant?.id ?? null,
          role: data.participant?.role ?? null,
          isHost: data.isHost === true,
          isInvited: data.isInvited === true,
        });

        navigate(`/s/${code}/room`, { replace: true });
      } catch (err) {
        logJoinWarning(code, 'join-failed', {
          error: err,
        });
        setError('Failed to join room');
      }
    }

    joinRoom();
  }, [code, getToken, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-bg">
        <div className="bg-panel border border-line rounded-xl p-6 max-w-md w-full text-center shadow-md">
          <h2 className="font-display text-xl font-medium text-warn mb-2">Unable to Join</h2>
          <p className="text-text-dim mb-4">{error}</p>
          <button
            onClick={() => navigate(code ? `/s/${code}` : '/')}
            className="btn"
          >
            Back to Event
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="text-text-dim">Joining session...</div>
    </div>
  );
}
